
import axios from "axios";
import { Helmet } from 'react-helmet';
import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";

import { OrderType } from "../../types/OrderType";
import Footer from "../../components/footer.component";
import { AppLoader } from "../../components/app.loader";
import { SERVER_API_ORDERS } from "../../redux/api.constants";
import { Grid, InputLabel, Typography } from "@material-ui/core";

import styles from './styles.module.scss';
import classNames from "classnames";

export const OrderTrackingPage = () => {
    const [order, setOrder] = useState<OrderType>();
    const [isLoading, setIsLoading] = useState(false);
    const [duplicatedOrders, setDuplicatedOrders] = useState<OrderType[]>([]);
    const { uid } = useParams<{ uid: string }>();

    useEffect(() => {
        const getOrder = async () => {
            try {
                setIsLoading(true)
                const response = await axios.get(`${SERVER_API_ORDERS}/get-one-order/${uid}`);
                setOrder(response.data.order);
                setDuplicatedOrders(response.data.duplicatedOrders)

            } catch (error) {
                window.location.href = "/";
                console.error('Error during Getting Order:', error);
            } finally {
                setIsLoading(false)
            }
        }

        getOrder();

    }, [uid]);

    const doNotShowTrackingLinkArray = ['0010669245'];

    const trackingNumber = order?.trackingNumber;
    const doNotShowTrackingLinkFor =
        trackingNumber && doNotShowTrackingLinkArray.some(link => trackingNumber.startsWith(link));

    const displayTracking = order?.defaultTrackLink &&
        order?.trackingNumber &&
        !doNotShowTrackingLinkFor;

    const carrierLogoUrl = order?.logo

    return (
        <React.Fragment>
            <Helmet>
                <title>{"Manage Orders | Eurotech"}</title>
                <meta name="description"
                    content={"Eurotech has been the premier representative for quality and ergonomic design in office chairs since 1979. Our commitment to design, function, and form has resulted in over 1 billion dollars in cumulative sales. See the Eurotech difference for yourself."} />
            </Helmet>
            <Grid container className={styles.wrapper}>
                <Grid item xs={12} className={styles.content}>
                    <article className={styles.content_wrapper}>
                        <Typography variant="h5" className={styles.subtitle}>
                            ORDER TRACKING
                        </Typography>
                        <Typography variant="h2" className={styles.title}>
                            Your Order Status
                        </Typography>
                        <Typography variant="caption" className={styles.description}>
                            Here’s the latest update on your order. Below, you’ll find details including order status, estimated<br /> delivery date, and shipping carrier information.
                        </Typography>

                        <div className={styles.inputContainer}>
                            <div className={styles.inputContainerColumn}>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Customer Number
                                    </InputLabel>
                                    <div className={styles.defaultValueDisplaying}>
                                        {order?.customerNumber || "..."}
                                    </div>
                                    <span className={styles.line} />
                                </div>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Ship-To Zip Code
                                    </InputLabel>
                                    <div className={styles.defaultValueDisplaying}>
                                        {order?.shippingZipCode || "..."}
                                    </div>
                                    <span className={styles.line} />
                                </div>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Raynor Order Number
                                    </InputLabel>
                                    <div className={styles.defaultValueDisplaying}>
                                        {order?.raynorOrderNum || "..."}
                                    </div>
                                    <span className={styles.line} />
                                </div>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Tracking Link{duplicatedOrders && 's'}
                                    </InputLabel>
                                    {displayTracking ? (
                                        <>
                                            <a
                                                target="_blank"
                                                href={`${order?.defaultTrackLink}${order.trackingNumber}`}
                                                className={styles.linkValueDisplaying}>
                                                {duplicatedOrders ? order.trackingNumber : 'Track your shipment'}
                                            </a>
                                            {!!duplicatedOrders.length && duplicatedOrders.map(duplicatedOrder => (
                                                <a
                                                    target="_blank"
                                                    key={order._id}
                                                    href={`${order?.defaultTrackLink}${duplicatedOrder.trackingNumber}`}
                                                    className={classNames(styles.linkValueDisplaying, styles.linkDuplicatesValueDisplaying)}>
                                                    {duplicatedOrder.trackingNumber}
                                                </a>
                                            ))}
                                        </>
                                    ) : (
                                        <div className={styles.mailToDisplaying}>
                                            <span>We have received your order and it is being processed.<br />
                                                For additional information email </span><a href="mailto:customerservice@raynorgroup.com">customerservice@raynorgroup.com</a>
                                        </div>
                                    )}
                                    <span className={styles.line} />
                                </div>
                            </div>

                            <div className={styles.inputContainerColumn}>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Customer PO Number
                                    </InputLabel>
                                    <div className={styles.defaultValueDisplaying}>
                                        {order?.customerPoNum || "..."}
                                    </div>
                                    <span className={styles.line} />
                                </div>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Carrier
                                    </InputLabel>
                                    <div className={styles.carrierValueDisplaying}>
                                        {!!carrierLogoUrl?.length &&
                                            <img src={carrierLogoUrl} className={styles.carrierLogo} alt="Carrier Logo" />
                                        }
                                        <div className={styles.carrierValue}>
                                            {order?.displayTrackLink || "..."}
                                        </div>
                                    </div>
                                    <span className={styles.line} />
                                </div>
                                <div className={styles.item}>
                                    <InputLabel shrink className={styles.label}>
                                        Tracking Number
                                    </InputLabel>
                                    {trackingNumber ? (
                                        <div className={styles.defaultValueDisplaying}>
                                            {trackingNumber.startsWith("0010669245") ? 'Raynor Internal Tracking' : trackingNumber || '...'}
                                        </div>
                                    ) : (
                                        <div className={styles.mailToDisplaying}>
                                            <span>We have received your order and it is being processed.<br />
                                                For additional information email </span><a href="mailto:customerservice@raynorgroup.com">customerservice@raynorgroup.com</a>
                                        </div>
                                    )}

                                    <span className={styles.line} />
                                </div>
                            </div>

                        </div>

                    </article>
                    {isLoading && <div className={styles.content_loader}><AppLoader /></div>}
                </Grid>

                <Footer />
            </Grid>
        </React.Fragment>
    )
}